import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { SearchBar } from '../components/SearchBar';
import { TrainerCard } from '../components/TrainerCard';
import { Button } from '../components/ui/button';
import { Loader2, Dumbbell } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Home() {
  const [trainers, setTrainers] = useState([]);
  const [topRated, setTopRated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [searchParams, setSearchParams] = useState({ search: '', location: '' });

  const fetchTrainers = useCallback(async (params = {}) => {
    try {
      setSearching(true);
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      if (params.location) queryParams.append('location', params.location);
      
      const [trainersRes, topRatedRes] = await Promise.all([
        axios.get(`${API_URL}/trainers${queryParams.toString() ? `?${queryParams}` : ''}`),
        axios.get(`${API_URL}/trainers/top-rated?limit=6`)
      ]);
      
      setTrainers(trainersRes.data);
      setTopRated(topRatedRes.data);
    } catch (error) {
      console.error('Error fetching trainers:', error);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    fetchTrainers();
  }, [fetchTrainers]);

  const handleSearch = (params) => {
    setSearchParams(params);
    fetchTrainers(params);
  };

  const isSearching = searchParams.search || searchParams.location;

  return (
    <div className="min-h-screen bg-[#0B0B0B]" data-testid="home-page">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Hero Content */}
          <div className="text-center mb-10 animate-fade-in-up">
            <h1 className="font-heading text-5xl md:text-7xl font-bold tracking-tighter leading-none uppercase text-white mb-4">
              Find Your <span className="text-primary">Trainer</span>
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Connect with elite personal trainers in your area. Transform your fitness journey today.
            </p>
          </div>

          {/* Search Bar */}
          <div className="animate-fade-in-up animation-delay-200">
            <SearchBar onSearch={handleSearch} />
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="px-4 sm:px-6 lg:px-8 pb-16">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : (
            <>
              {/* Search Results */}
              {isSearching ? (
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight uppercase text-white mb-6">
                    Search Results
                    {searching && <Loader2 className="inline-block ml-3 w-5 h-5 animate-spin" />}
                  </h2>
                  {trainers.length === 0 ? (
                    <div className="text-center py-16">
                      <p className="text-gray-400 text-lg">No trainers found matching your search.</p>
                      <p className="text-gray-500 text-sm mt-2">Try adjusting your filters or search terms.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      {trainers.map((trainer, index) => (
                        <div
                          key={trainer.id}
                          className="animate-fade-in-up"
                          style={{ animationDelay: `${index * 100}ms` }}
                        >
                          <TrainerCard trainer={trainer} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                /* Top Rated Section */
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight uppercase text-white mb-6">
                    Top Rated Trainers
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {topRated.map((trainer, index) => (
                      <div
                        key={trainer.id}
                        className="animate-fade-in-up"
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        <TrainerCard trainer={trainer} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Become a Trainer CTA */}
      <section className="px-4 sm:px-6 lg:px-8 pb-16">
        <div className="max-w-3xl mx-auto">
          <div className="bg-[#141414] rounded-2xl p-8 border border-white/5 text-center">
            <Dumbbell className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold uppercase text-white mb-3">
              Are You a Trainer?
            </h2>
            <p className="text-gray-400 mb-6 max-w-lg mx-auto">
              Join our platform and connect with clients looking for expert trainers like you. Grow your business today.
            </p>
            <Link to="/become-a-trainer">
              <Button 
                className="bg-primary text-white font-bold uppercase tracking-wider px-8 py-4 rounded-full hover:shadow-[0_0_30px_rgba(59,130,246,0.6)] transition-all transform hover:-translate-y-1"
                data-testid="become-trainer-cta"
              >
                Join the Platform
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
